import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import {
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Phone,
  Mail,
  FileText,
  Calendar,
  Building,
  Truck,
  Search,
  RefreshCw,
  Eye,
  ChevronRight,
  AlertCircle,
  Car
} from 'lucide-react';
import TablePagination from '../../components/TablePagination';
import GenericBulkActionsBar from '../../components/GenericBulkActionsBar';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingDriversManagement = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [rejectedDrivers, setRejectedDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPagePending, setCurrentPagePending] = useState(1);
  const [currentPageRejected, setCurrentPageRejected] = useState(1);
  const itemsPerPage = 10;
  
  // Bulk selection state
  const [selectedPending, setSelectedPending] = useState([]);
  const [selectAllPending, setSelectAllPending] = useState(false);
  
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: 'Bearer cookie-auth' };

      // Fetch pending drivers
      const pendingRes = await axios.get(`${API}/drivers/pending/list`, { headers });
      setPendingDrivers(pendingRes.data.drivers || []);

      // Fetch all drivers to filter rejected ones
      try {
        const allRes = await axios.get(`${API}/drivers?include_inactive=true`, { headers });
        const rejected = allRes.data.filter(d => d.status === 'rejected');
        setRejectedDrivers(rejected);
      } catch (e) {
        logger.debug('Could not fetch rejected drivers list');
        setRejectedDrivers([]);
      }

    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les chauffeurs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const handleValidate = async () => {
    if (!selectedDriver) return;

    try {
      setActionLoading(true);
      const payload = actionType === 'approve' 
        ? { status: 'approved' }
        : { status: 'rejected', reason: rejectionReason || 'Non spécifié' };

      await axios.put(
        `${API}/drivers/${selectedDriver.id}/validate`,
        payload,
        {}
      );

      toast({
        title: actionType === 'approve' ? '✅ Chauffeur approuvé' : '❌ Chauffeur rejeté',
        description: actionType === 'approve' 
          ? `${selectedDriver.firstName} ${selectedDriver.lastName} peut maintenant se connecter`
          : `La demande de ${selectedDriver.firstName} ${selectedDriver.lastName} a été rejetée`
      });

      setShowActionModal(false);
      setShowDetailModal(false);
      setSelectedDriver(null);
      setRejectionReason('');
      fetchDrivers();

    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openDetailModal = (driver) => {
    setSelectedDriver(driver);
    setShowDetailModal(true);
  };

  const openActionModal = (type) => {
    setActionType(type);
    setShowActionModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccountTypeBadge = (type) => {
    if (type === 'company') {
      return <Badge className="bg-purple-100 text-purple-800">Société de transport</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800">Chauffeur indépendant</Badge>;
  };

  const filteredPendingDrivers = pendingDrivers.filter(driver => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      driver.firstName?.toLowerCase().includes(query) ||
      driver.lastName?.toLowerCase().includes(query) ||
      driver.email?.toLowerCase().includes(query) ||
      driver.phone?.includes(query) ||
      driver.companyName?.toLowerCase().includes(query)
    );
  });

  // Bulk selection handlers for pending drivers
  const handleSelectPending = (driverId) => {
    setSelectedPending(prev => {
      const newSelection = prev.includes(driverId) 
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId];
      
      // Mettre à jour l'état selectAllPending en fonction de la sélection
      const currentPageDrivers = filteredPendingDrivers
        .slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage);
      
      const allSelected = currentPageDrivers.length > 0 && 
        currentPageDrivers.every(d => newSelection.includes(d.id));
      setSelectAllPending(allSelected);
      
      return newSelection;
    });
  };

  const handleSelectAllPending = () => {
    const currentPageDrivers = filteredPendingDrivers
      .slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage);
    
    if (selectAllPending) {
      setSelectedPending(prev => prev.filter(id => !currentPageDrivers.find(d => d.id === id)));
      setSelectAllPending(false);
    } else {
      const newIds = currentPageDrivers.map(d => d.id);
      setSelectedPending(prev => [...new Set([...prev, ...newIds])]);
      setSelectAllPending(true);
    }
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir approuver ${selectedPending.length} demande(s) ?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bulk-actions/drivers/validate`, {
        driverIds: selectedPending,
        action: 'approve'
      });
      toast({
        title: "✅ Approbation réussie",
        description: response.data.message
      });
      setSelectedPending([]);
      setSelectAllPending(false);
      fetchDrivers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'approbation",
        variant: "destructive"
      });
    }
  };

  const handleBulkReject = async () => {
    const reason = window.prompt("Raison du rejet (optionnel):");
    if (reason === null) return; // User cancelled
    
    try {
      const response = await axios.post(`${API}/bulk-actions/drivers/validate`, {
        driverIds: selectedPending,
        action: 'reject',
        reason: reason || 'Rejet groupé'
      });
      toast({
        title: "❌ Rejet effectué",
        description: response.data.message
      });
      setSelectedPending([]);
      setSelectAllPending(false);
      fetchDrivers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors du rejet",
        variant: "destructive"
      });
    }
  };

  // Reset selectAll when page changes
  useEffect(() => {
    const currentPageDrivers = filteredPendingDrivers
      .slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage);
    const allSelected = currentPageDrivers.length > 0 && 
      currentPageDrivers.every(d => selectedPending.includes(d.id));
    setSelectAllPending(allSelected);
  }, [currentPagePending, filteredPendingDrivers, selectedPending]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Validation des Chauffeurs</h1>
          <p className="text-gray-500 mt-1">Gérer les inscriptions des chauffeurs en attente</p>
        </div>
        <Button onClick={fetchDrivers} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En attente</p>
                <p className="text-xl md:text-3xl font-bold text-amber-600">{pendingDrivers.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approuvés ce mois</p>
                <p className="text-xl md:text-3xl font-bold text-green-600">-</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rejetés</p>
                <p className="text-xl md:text-3xl font-bold text-red-600">{rejectedDrivers.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Tabs */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email, téléphone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                En attente ({pendingDrivers.length})
              </TabsTrigger>
              <TabsTrigger value="rejected" className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Rejetés ({rejectedDrivers.length})
              </TabsTrigger>
            </TabsList>

            {/* Pending Tab */}
            <TabsContent value="pending">
              {filteredPendingDrivers.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Aucune demande en attente</p>
                  <p className="text-gray-400 text-sm mt-1">Toutes les inscriptions ont été traitées</p>
                </div>
              ) : (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectAllPending}
                          onCheckedChange={handleSelectAllPending}
                        />
                      </TableHead>
                      <TableHead>Chauffeur</TableHead>
                      <TableHead>Type de compte</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Permis</TableHead>
                      <TableHead>Date d'inscription</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPendingDrivers
                      .slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage)
                      .map((driver) => (
                      <TableRow key={driver.id} className={`hover:bg-gray-50 ${selectedPending.includes(driver.id) ? 'bg-amber-50' : ''}`}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedPending.includes(driver.id)}
                            onCheckedChange={() => handleSelectPending(driver.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium">{driver.firstName} {driver.lastName}</p>
                              {driver.companyName && (
                                <p className="text-sm text-gray-500">{driver.companyName}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getAccountTypeBadge(driver.accountType)}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {driver.phone}
                            </p>
                            {driver.email && (
                              <p className="text-sm flex items-center gap-1 text-gray-500">
                                <Mail className="w-3 h-3" /> {driver.email}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{driver.permitType}</p>
                            <p className="text-xs text-gray-500">{driver.permitNumber}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{formatDate(driver.createdAt)}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailModal(driver)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setSelectedDriver(driver);
                                openActionModal('approve');
                              }}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedDriver(driver);
                                openActionModal('reject');
                              }}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <TablePagination
                  currentPage={currentPagePending}
                  totalItems={filteredPendingDrivers.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPagePending}
                  itemLabel="demandes en attente"
                />
                </>
              )}
            </TabsContent>

            {/* Rejected Tab */}
            <TabsContent value="rejected">
              {rejectedDrivers.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune demande rejetée</p>
                </div>
              ) : (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chauffeur</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Raison du rejet</TableHead>
                      <TableHead>Date de rejet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rejectedDrivers
                      .slice((currentPageRejected - 1) * itemsPerPage, currentPageRejected * itemsPerPage)
                      .map((driver) => (
                      <TableRow key={driver.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium">{driver.firstName} {driver.lastName}</p>
                              {driver.companyName && (
                                <p className="text-sm text-gray-500">{driver.companyName}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{driver.email || driver.phone}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-red-600">{driver.rejectionReason || 'Non spécifié'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{formatDate(driver.rejectedAt)}</p>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <TablePagination
                  currentPage={currentPageRejected}
                  totalItems={rejectedDrivers.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPageRejected}
                  itemLabel="demandes rejetées"
                />
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Détails du chauffeur
            </DialogTitle>
          </DialogHeader>
          
          {selectedDriver && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Nom complet</label>
                  <p className="font-medium">{selectedDriver.firstName} {selectedDriver.lastName}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Type de compte</label>
                  <p>{getAccountTypeBadge(selectedDriver.accountType)}</p>
                </div>
                {selectedDriver.companyName && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-500">Nom de la société</label>
                    <p className="font-medium flex items-center gap-2">
                      <Building className="w-4 h-4" />
                      {selectedDriver.companyName}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Contact
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Téléphone</label>
                    <p className="font-medium">{selectedDriver.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p className="font-medium">{selectedDriver.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Permis */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Permis de conduire
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Type</label>
                    <p className="font-medium">{selectedDriver.permitType}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Numéro</label>
                    <p className="font-medium">{selectedDriver.permitNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Expiration</label>
                    <p className="font-medium">{formatDate(selectedDriver.permitExpirationDate)}</p>
                  </div>
                </div>
              </div>

              {/* CNI */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Carte d'identité
                </h4>
                <div>
                  <label className="text-sm text-gray-500">Numéro CNI</label>
                  <p className="font-medium">{selectedDriver.cniNumber || 'N/A'}</p>
                </div>
              </div>

              {/* Documents société */}
              {selectedDriver.accountType === 'company' && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" /> Documents société
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">RCCM</label>
                      <p className="font-medium">{selectedDriver.rccm || 'Non fourni'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">DFE</label>
                      <p className="font-medium">{selectedDriver.dfe || 'Non fourni'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Carte de transport</label>
                      <p className="font-medium">{selectedDriver.transportCard || 'Non fourni'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents uploadés */}
              {selectedDriver.documents && selectedDriver.documents.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documents uploadés ({selectedDriver.documents.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDriver.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="text-sm">{doc.type} - {doc.fileName}</span>
                        <span className="text-xs text-gray-500">{formatDate(doc.uploadedAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-sm text-gray-500 border-t pt-4">
                <p>Inscrit le {formatDate(selectedDriver.createdAt)} via {selectedDriver.createdFrom === 'web_app' ? 'le site web' : "l'application mobile"}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button
              variant="destructive"
              onClick={() => openActionModal('reject')}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rejeter
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => openActionModal('approve')}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approuver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? '✅ Approuver le chauffeur' : '❌ Rejeter la demande'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? `Voulez-vous approuver l'inscription de ${selectedDriver?.firstName} ${selectedDriver?.lastName} ? Il pourra se connecter immédiatement.`
                : `Voulez-vous rejeter la demande de ${selectedDriver?.firstName} ${selectedDriver?.lastName} ?`}
            </DialogDescription>
          </DialogHeader>

          {actionType === 'reject' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Raison du rejet (optionnel)</label>
              <Textarea
                placeholder="Ex: Documents incomplets, permis expiré..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleValidate}
              disabled={actionLoading}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
            >
              {actionLoading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              {actionType === 'approve' ? 'Confirmer l\'approbation' : 'Confirmer le rejet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Actions Bar */}
      <GenericBulkActionsBar
        entityType="pending_drivers"
        selectedCount={selectedPending.length}
        onClearSelection={() => { setSelectedPending([]); setSelectAllPending(false); }}
        onApprove={handleBulkApprove}
        onReject={handleBulkReject}
      />
    </div>
  );
};

export default PendingDriversManagement;
